import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Box, Button, Divider, Paper, Typography } from 'presentation/components/atoms'
import { NoItemPaper } from './styles'
import { formatMoney } from 'utils/functionUtils'
import { CheckboxField, MaskedTextField, ValuesSummary } from 'presentation/components/molecules'
import { MethodPayment } from './components/methodPayment/methodPayment'
import { useFeatures } from 'main/hooks/useFeatures'
import { IOrder } from '@/presentation/pages/recharge/rechargePaymentConfirmation/hooks/useRequestInfos/utils/infosData'
import { Theme } from '@mui/material'

interface PaymentProps {
  isEmpty: boolean
  order: IOrder
  setBalanceToBeUsed: (balance: number) => void
  balanceToBeUsed: number
  setUseBalanceProjection: (projection: boolean) => void
  shouldUseProjection: boolean
  confirmOrder: () => Promise<void>
  setMethod: (method: 'PIX' | 'SLIP') => void
  method: 'PIX' | 'SLIP'
}

export const Payment = ({
  isEmpty,
  order,
  balanceToBeUsed,
  setBalanceToBeUsed,
  confirmOrder,
  setMethod,
  shouldUseProjection,
  setUseBalanceProjection,
  method,
}: PaymentProps) => {
  const { pathname } = useLocation()

  const { features } = useFeatures()

  const [walletUsageError, setWalletUsageError] = useState('')

  if (isEmpty) {
    return (
      <NoItemPaper>
        <Typography variant='h2'>Pagamento</Typography>
        <Divider sx={{ margin: '32px 0' }} />
        <Typography variant='label2'>Aqui estarão as informações e valores do seu pedido.</Typography>
        <br />
        <Typography variant='label2'>Adicione ao menos um item aop carrinho para visualizar.</Typography>
      </NoItemPaper>
    )
  } else
    return (
      <Paper
        sx={(theme: { palette: { grey: any[] } }) => ({ padding: '16px', backgroundColor: theme.palette.grey[50] })}
      >
        <Typography variant='h2'>Pagamento</Typography>
        {features.methodPayments ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column', margin: '32px 0 12px' }}
          >
            <Typography style={{ marginBottom: 24 }} variant='subtitle2'>
              Método de pagamento
            </Typography>

            <Box sx={{ display: 'flex', gap: '8px' }}>
              <MethodPayment checked={method === 'SLIP'} name='Boleto' onClick={() => setMethod('SLIP')} />
              <MethodPayment checked={method === 'PIX'} name='Pix' onClick={() => setMethod('PIX')} />
            </Box>
          </Box>
        ) : null}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', margin: '32px 0 12px' }}>
          <Typography variant='subtitle2'>Saldo Carteira digital</Typography>
          <p>
            <small>R$</small> {formatMoney(order?.accountBalance)}
          </p>
        </Box>
        <Typography variant='label2' sx={{ fontWeight: 400, marginBottom: '16px', display: 'block' }}>
          Para usar a Carteira Digital no pagamento deste pedido, digite o valor abaixo.
        </Typography>
        <MaskedTextField
          label='Valor de uso da Carteira Digital'
          placeholder='R$ 00,00'
          value={String(balanceToBeUsed)}
          helperText={walletUsageError}
          onChange={(value) => {
            let newValue: number = Number(value) || 0
            if (Number(value) > order?.totalBeforeBalanceUsage) {
              newValue = order?.totalBeforeBalanceUsage
              setWalletUsageError(
                `Você não pode exceder o valor total a ser pago de R$ ${formatMoney(order?.totalBeforeBalanceUsage)}`,
              )
            } else if (Number(value) > order?.accountBalance) {
              newValue = order?.accountBalance
              setWalletUsageError(`Você não pode exceder o seu saldo atual de R$ ${formatMoney(order?.accountBalance)}`)
            }
            setBalanceToBeUsed(newValue)
          }}
          mask={[
            { mask: '' },
            {
              mask: 'R$ num',
              lazy: true,
              blocks: {
                num: {
                  mask: Number,
                  scale: 2,
                  thousandsSeparator: '.',
                  radix: ',',
                  mapToRadix: ['.'],
                },
              },
            },
          ]}
          disabled={order?.accountBalance === 0 || order?.accountBalance === 0}
        />
        <Typography variant='subtitle2' sx={{ marginTop: '32px' }}>
          Resumo da compra
        </Typography>
        <Divider sx={{ margin: '32px 0' }} />
        <ValuesSummary
          values={[
            { label: 'Taxas administrativas', value: order?.administrateFee, size: 'md', hideZero: true },
            { label: 'Taxa de repasse', value: order?.transferFeeValue, size: 'md', hideZero: true },
            features.ticketingBalance
              ? {
                  label: 'Conta corrente total',
                  value: order?.ticketingBalanceValue,
                  size: 'md',
                  hideZero: true,
                  isNegative: true,
                  color: 'green',
                }
              : null,
            {
              label: 'Retenção ISS',
              value: order?.issFeeValue,
              size: 'md',
              hideZero: true,
              isNegative: true,
              color: 'green',
            },
            { label: 'Carteira digital', value: Number(balanceToBeUsed), size: 'md', isNegative: true, color: 'green' },
          ]}
        />
        <Divider sx={{ margin: '32px 0' }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontWeight: 600 }}>
          <Typography sx={{ fontWeight: 600 }}>Total a ser pago: </Typography>
          <Typography variant='h4' sx={{ fontWeight: 600 }}>
            <span style={{ fontWeight: 400 }}>R$ </span> {formatMoney(order?.totalBeforeBalanceUsage - balanceToBeUsed)}
          </Typography>
        </Box>
        {pathname !== '/cartoes/novo/segunda_confirmacao' ? (
          <Box
            sx={(theme: Theme) => ({
              backgroundColor: shouldUseProjection ? 'transparent' : theme.palette.benefits.snack.light,
              display: 'flex',
              alignItems: 'center',
              margin: '22px 0 0 -16px',
              width: 'calc(100% + 32px)',
              height: '56px',
              padding: '0 16px ',
              transition: 'all .25s ease-in',
              '& label': {
                marginBottom: 0 + '!important',
                color: theme.palette.common.black,
                fontSize: '18px',
              },
            })}
          >
            <CheckboxField
              label={
                <>
                  <b>projeção de saldo</b>Quero fazer a{' '}
                </>
              }
              onChange={(e: any) => setUseBalanceProjection(e.target.checked)}
              value={shouldUseProjection}
            />
          </Box>
        ) : null}
        <Button
          variant='contained'
          color='success'
          fullWidth
          sx={{ color: 'white', marginTop: '32px' }}
          onClick={async () => await confirmOrder()}
          disabled={!method && features.methodPayments}
        >
          Concluir pedido
        </Button>
      </Paper>
    )
}
